import { useEffect, useRef } from "react"

import { RulePopover } from "@/components/rule-popover"
import { SkillBadge } from "@/components/skill-badge"
import type { AnimationPlayer } from "@/types"

interface PlayerPopoverProps {
  player: AnimationPlayer | null
  position: { x: number; y: number } | null
  status: "standing" | "prone" | "stunned" | null
  onClose: () => void
}

/**
 * Click-triggered player inspection popover for the replay viewer.
 *
 * Shows: player name, position, stat line (MA/ST/AG/PA/AV), and skill badges.
 * Does NOT interact with playback state — clicking a token never pauses replay.
 *
 * Closes on: click outside or Escape key.
 * Positioned near the clicked token via fixed coordinates derived from DOMRect.
 */
export function PlayerPopover({
  player,
  position,
  status,
  onClose,
}: PlayerPopoverProps) {
  const popoverRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!player) return

    function handleMouseDown(e: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        onClose()
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose()
      }
    }

    document.addEventListener("mousedown", handleMouseDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("mousedown", handleMouseDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [player, onClose])

  if (!player || !position) return null

  // Keep popover on-screen: flip to above token if in the lower half
  const isLowerHalf = position.y > window.innerHeight / 2
  const top = isLowerHalf ? position.y - 170 : position.y + 20
  const left = Math.min(Math.max(position.x - 90, 8), window.innerWidth - 220)

  return (
    <div
      ref={popoverRef}
      data-testid="player-popover"
      style={{
        position: "fixed",
        left: `${left}px`,
        top: `${top}px`,
        zIndex: 50,
        background: "oklch(0.12 0.005 230)",
        border: "2px solid oklch(0.25 0.01 230)",
        boxShadow: "0 4px 16px oklch(0 0 0 / 0.5)",
        padding: "10px 14px",
        minWidth: "180px",
        maxWidth: "240px",
      }}
    >
      {/* Player name */}
      <div
        className="font-display"
        style={{
          color: "oklch(0.80 0.16 85)",
          fontSize: "16px",
          fontWeight: 700,
          lineHeight: 1.1,
          marginBottom: "2px",
        }}
      >
        {player.name}
      </div>

      {/* Position */}
      <div
        style={{
          fontSize: "11px",
          color: "oklch(0.50 0.01 230)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          marginBottom: "8px",
        }}
      >
        {player.position}
      </div>

      {/* Status badge (prone/stunned only) */}
      {status === "stunned" && (
        <RulePopover category="injury" ruleKey="stunned">
          <div
            style={{
              fontSize: "10px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              padding: "2px 6px",
              marginBottom: "8px",
              background: "oklch(0.28 0.08 28)",
              border: "1px solid oklch(0.45 0.12 28)",
              color: "oklch(0.78 0.12 28)",
              cursor: "pointer",
            }}
          >
            Stunned — Cannot Act
          </div>
        </RulePopover>
      )}
      {status === "prone" && (
        <RulePopover category="injury" ruleKey="prone">
          <div
            style={{
              fontSize: "10px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              padding: "2px 6px",
              marginBottom: "8px",
              background: "oklch(0.20 0.02 230)",
              border: "1px solid oklch(0.30 0.04 230)",
              color: "oklch(0.70 0.03 230)",
              cursor: "pointer",
            }}
          >
            Prone — Knocked Down
          </div>
        </RulePopover>
      )}

      {/* Stat line: MA / ST / AG / PA / AV */}
      <div
        style={{
          display: "flex",
          gap: "4px",
          marginBottom: "8px",
        }}
      >
        {(
          [
            { label: "MA", value: player.ma },
            { label: "ST", value: player.st },
            { label: "AG", value: player.ag },
            { label: "PA", value: player.pa ?? "—" },
            { label: "AV", value: player.av },
          ] as { label: string; value: number | string }[]
        ).map(({ label, value }) => (
          <div
            key={label}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1px",
              flex: 1,
              background: "oklch(0.14 0.005 230)",
              padding: "3px 2px",
            }}
          >
            <span
              style={{
                fontSize: "9px",
                color: "oklch(0.50 0.01 230)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {label}
            </span>
            <span
              style={{
                fontSize: "13px",
                fontWeight: 700,
                color: "oklch(0.85 0.03 80)",
              }}
            >
              {value}
            </span>
          </div>
        ))}
      </div>

      {/* Skill badges */}
      {player.skills.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
          {player.skills.map((skill) => (
            <SkillBadge
              key={skill}
              name={skill}
              style={{
                fontSize: "9px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                padding: "1px 5px",
                border: "1px solid oklch(0.25 0.01 230)",
                background: "oklch(0.16 0.005 230)",
                color: "oklch(0.70 0.03 80)",
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}
